import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Mail, Phone, MapPin, X, MessageSquare, Send, Check } from 'lucide-react';

const AgencyContactForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    empresa: '',
    email: '',
    telefono: '',
    ubicacion: '',
    mensaje: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Realizar la llamada a la API
      const response = await fetch('https://backend-production-1786.up.railway.app/api/agency-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Error al enviar el formulario');
      }
      
      // Mostrar éxito
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error:', error);
      // Aquí podrías mostrar un mensaje de error al usuario
    } finally {
      setLoading(false);
    }
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      y: -20,
      transition: { duration: 0.2 }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        type: "spring", 
        damping: 20, 
        stiffness: 100 
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.9,
      transition: { duration: 0.2 }
    }
  };

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div 
          variants={contentVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="bg-white rounded-xl sm:rounded-2xl w-full max-w-sm sm:max-w-md relative overflow-hidden shadow-xl"
          style={{ width: "calc(100% - 20px)", maxHeight: "calc(100vh - 40px)", overflowY: "auto" }}
          onClick={(e) => e.stopPropagation()}
        >
          <motion.button 
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors z-10 bg-white/80 backdrop-blur-sm p-1 rounded-full"
          >
            <X size={18} />
          </motion.button>

          <div className="p-5 sm:p-6">
            <motion.div 
              className="text-center mb-5 sm:mb-6"
              variants={fadeIn}
            >
              <div className="bg-gradient-to-br from-indigo-50 to-blue-100 w-16 h-16 sm:w-18 sm:h-18 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <Building2 className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                Únete como Constructora
              </h2>
              <p className="text-gray-600 mt-2 text-sm sm:text-base max-w-xs mx-auto">
                Déjanos tus datos y un asesor se pondrá en contacto contigo
              </p>
            </motion.div>

            <AnimatePresence mode="wait">
              {success ? (
                <motion.div 
                  key="success"
                  variants={fadeIn}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="bg-green-50 p-5 rounded-xl border border-green-100 text-center"
                >
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-green-700 mb-2">¡Solicitud Enviada!</h3>
                  <p className="text-green-600 text-sm">
                    Gracias por tu interés. Nuestro equipo se pondrá en contacto contigo pronto.
                  </p>
                </motion.div>
              ) : (
                <motion.form 
                  key="form"
                  onSubmit={handleSubmit} 
                  className="space-y-3"
                  variants={fadeIn}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <motion.div 
                    className="relative"
                    variants={fadeIn}
                    transition={{ delay: 0.1 }}
                  >
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500" size={18} />
                    <input
                      type="text"
                      placeholder="Nombre de la Constructora"
                      value={formData.empresa}
                      onChange={(e) => setFormData({...formData, empresa: e.target.value})}
                      className="w-full pl-9 pr-3 py-2.5 text-sm sm:text-base border-2 border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                    />
                  </motion.div>

                  <motion.div 
                    className="relative"
                    variants={fadeIn}
                    transition={{ delay: 0.2 }}
                  >
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500" size={18} />
                    <input
                      type="email"
                      placeholder="Email corporativo"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full pl-9 pr-3 py-2.5 text-sm sm:text-base border-2 border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                    />
                  </motion.div>

                  <motion.div 
                    className="relative"
                    variants={fadeIn}
                    transition={{ delay: 0.3 }}
                  >
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500" size={18} />
                    <input
                      type="tel"
                      placeholder="Teléfono de contacto"
                      value={formData.telefono}
                      onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                      className="w-full pl-9 pr-3 py-2.5 text-sm sm:text-base border-2 border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                    />
                  </motion.div>

                  <motion.div 
                    className="relative"
                    variants={fadeIn}
                    transition={{ delay: 0.4 }}
                  >
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500" size={18} />
                    <input
                      type="text"
                      placeholder="Ubicación principal"
                      value={formData.ubicacion}
                      onChange={(e) => setFormData({...formData, ubicacion: e.target.value})}
                      className="w-full pl-9 pr-3 py-2.5 text-sm sm:text-base border-2 border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      required
                    />
                  </motion.div>

                  <motion.div 
                    className="relative"
                    variants={fadeIn}
                    transition={{ delay: 0.5 }}
                  >
                    <MessageSquare className="absolute left-3 top-3 text-indigo-500" size={18} />
                    <textarea
                      placeholder="Mensaje adicional (opcional)"
                      value={formData.mensaje}
                      onChange={(e) => setFormData({...formData, mensaje: e.target.value})}
                      className="w-full pl-9 pr-3 py-2.5 text-sm sm:text-base border-2 border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors min-h-[90px] sm:min-h-[100px] resize-none"
                    />
                  </motion.div>

                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-2.5 rounded-lg font-medium transition-all duration-300 hover:shadow-lg shadow-md flex items-center justify-center gap-2 disabled:opacity-70 text-sm sm:text-base mt-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    variants={fadeIn}
                    transition={{ delay: 0.6 }}
                  >
                    {loading ? (
                      <>
                        <span className="animate-pulse">Enviando</span>
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
                        </span>
                      </>
                    ) : (
                      <>
                        Solicitar información
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AgencyContactForm;